import { ECONOMY } from '@/config/economy';
import type { CrateDestination } from '@/ui/shop-types';
import type { Player } from '@/entities/player';
import { CARDS, type CardContext, type CardDef } from '@/upgrades/cards';
import { priceOf } from '@/upgrades/pool';
import type { RunState } from './run-state';

/**
 * Ship ↔ run glue: the ship's stats are NOT stored — they are re-derived by
 * re-applying the owned card collection to a fresh ship (docs/03).
 */
export function cardContext(player: Player, run: RunState): CardContext {
  return {
    stats: player.stats,
    weapon: player.weapon,
    player,
    owned: (id) => run.ownedCards.reduce((n, c) => n + (c.id === id ? 1 : 0), 0),
  };
}

/** Spare-weapon BAG slots: base + Cargo Bay buys (docs/09 wave 4). */
export function bagCap(player: Player): number {
  return Math.min(ECONOMY.inventory.total, ECONOMY.inventory.start + player.stats.cargoSlots);
}

/** Weapons the ship can own: open sockets + bag slots. */
export function inventoryCap(player: Player): number {
  return player.weapon.capacity + bagCap(player);
}

/** Fresh ship + every owned card re-applied, then `equipped` re-socketed. */
export function rebuildFromCards(
  player: Player, run: RunState, equipped: ReadonlyArray<string>,
): void {
  player.resetRun();
  for (const card of run.ownedCards) card.apply(cardContext(player, run));
  player.setEquipped(equipped, run.inventory);
}

/** Spare weapons in the bag (owned but not in a socket). */
export function bagCount(player: Player, run: RunState): number {
  return run.inventory.length - player.weapon.equipped.length;
}

/**
 * Where a crate of `weaponId` lands if picked up now (docs/09 wave 4):
 * an EMPTY socket first, else the bag, else the swap-or-scrap slot; a
 * weapon already owned (or no room at all) pays its scrap value instead.
 */
export function crateDestination(player: Player, run: RunState, weaponId: string): CrateDestination {
  if (run.inventory.includes(weaponId) || run.pendingWeapon === weaponId) return 'scrap';
  if (player.weapon.equipped.length < player.weapon.capacity) return 'socket';
  if (bagCount(player, run) < bagCap(player)) return 'bag';
  return run.pendingWeapon === null ? 'full' : 'scrap';
}

/** The card a hangar padlock sells: Weapon Rack (socket) / Cargo Bay (bag). */
export function hangarCard(id: 'rack' | 'cargo'): CardDef {
  return CARDS.find((c) => c.id === id)!;
}

/** Red price of the next socket, null when all three are open. */
export function socketPrice(player: Player): number | null {
  return ECONOMY.socketPrice[player.weapon.rackSlots] ?? null;
}

/** Yellow price of the next bag slot (Cargo Bay card price), null at max. */
export function bagPrice(player: Player, run: RunState): number | null {
  if (bagCap(player) >= ECONOMY.inventory.total) return null;
  return priceOf(hangarCard('cargo'), run.roomInSector, player.stats.discountMul);
}
